import { lazy } from "react";

// Defers a dynamic import until first call and caches the resulting promise,
// so the bundler emits a separate chunk and repeat callers share one fetch.
export const lazyModule = <T>(loader: () => Promise<T>): (() => Promise<T>) => {
  let promise: Promise<T> | null = null;
  return () => {
    if (!promise) promise = loader();
    return promise;
  };
};

// Adapts modules that use named exports to the `{ default }` shape React.lazy
// requires. The conditional return type preserves the original component's
// props through inference, so callers can still pass typed props.
export const lazyNamed = <T, K extends keyof T>(loader: () => Promise<T>, name: K) =>
  lazy(() =>
    loader().then((m) => {
      const component = m?.[name];
      // A stale chunk served as the SPA catchall (HTML at 200) resolves to a
      // module with no usable export. Throw a typed error the boundary can
      // recognise instead of crashing on `undefined[name]`.
      if (!m || typeof m !== "object" || !component) {
        const err = new Error(
          `ChunkLoadError: missing export "${String(name)}" — likely a stale chunk after deploy`,
        );
        err.name = "ChunkLoadError";
        throw err;
      }
      return { default: component as React.ComponentType<unknown> };
    }),
  ) as unknown as T[K] extends React.ComponentType<infer P>
    ? React.LazyExoticComponent<React.ComponentType<P>>
    : never;
